import { todayIso, type AiBrokerResult, type AiFacts, type AiJob, type AiReportResult, type AiTableRow, type Finplan } from '@fin/shared';
import { useEffect, useRef, useState } from 'react';
import { put } from '../../lib/api.ts';
import { screenshotToJpeg, splitMoney } from '../../lib/ai.ts';
import { pct, rub } from '../../lib/format.ts';
import { invalidate } from '../../lib/query.ts';
import { Button, ErrorBox, Field, Icon, Input, NumberInput, cx, toast, useIsMobile } from '../../components/ui.tsx';

/** text from Codex with the server's amounts set in the money font */
export function Rich({ text }: { text: string }) {
  return <>{splitMoney(text).map((p, i) => (p.money ? <span key={i} className="money">{p.text}</span> : p.text))}</>;
}

const value = (v: number | null, unit: AiTableRow['unit']) =>
  v === null ? '—' : unit === 'money' ? rub(Math.round(v)) : unit === 'percent' ? pct(v, Math.abs(v) < 0.1 ? 1 : 0) : unit === 'delta' ? `${v > 0 ? '+' : ''}${pct(v, Math.abs(v) < 0.1 ? 1 : 0)}` : String(Math.round(v));

/** the stages a request goes through; the current one is marked, the passed ones ticked */
function Steps({ job }: { job: AiJob }) {
  const steps = job.kind === 'question' ? ['В очереди', 'Codex подбирает данные', 'Codex пишет ответ']
    : job.kind === 'broker' ? ['В очереди', 'Codex читает скриншоты'] : ['В очереди', 'Codex пишет отчёт'];
  const at = job.status === 'queued' && job.phase !== 'answer' ? 0
    : job.kind === 'question' && job.phase === 'answer' ? 2 : 1;
  const started = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const seconds = Math.round((now - Math.min(started.current, Date.parse(job.createdAt) || started.current)) / 1000);
  return (
    <div className="ai-wait" role="status" aria-live="polite">
      <ol className="ai-steps">
        {steps.map((s, i) => (
          <li key={s} className={cx(i < at && 'done', i === at && 'now')}>
            <span className="ai-step-mark" aria-hidden>{i < at ? <Icon name="check" size={14} /> : i === at ? <span className="spinner" /> : null}</span>
            <span>{s}{i === at && job.ahead > 0 ? <span className="muted"> · впереди {job.ahead}</span> : null}</span>
          </li>
        ))}
      </ol>
      <span className="muted small mono">{seconds} с</span>
      <div className="ai-sk" aria-hidden>
        <span className="skeleton sk-line" /><span className="skeleton sk-line" /><span className="skeleton sk-line ai-sk-short" />
      </div>
    </div>
  );
}

function Facts({ facts }: { facts: AiFacts }) {
  return (
    <section className="ai-facts" aria-label="Цифры отчёта">
      <h3>Цифры <span className="muted small">{facts.label}</span></h3>
      <ul>
        {facts.metrics.map((m) => (
          <li key={m.key} className="ledger-row">
            <span className="ledger-main">{m.label}</span>
            <span className={cx('ledger-amount', m.tone)}>{value(m.value, m.unit)}</span>
          </li>
        ))}
      </ul>
      {facts.tables.map((t) => (
        <details key={t.key} className="ai-table">
          <summary>{t.title}<span className="muted small"> {t.rows.length}</span></summary>
          <ul>
            {t.rows.map((r, i) => (
              <li key={i} className="ledger-row">
                <span className="ai-row-text">{r.label}{r.note ? <span className="muted small">{r.note}</span> : null}</span>
                <span className="ledger-amount">{value(r.value, r.unit)}</span>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </section>
  );
}

function Report({ r, facts }: { r: AiReportResult; facts: AiFacts | null }) {
  return (
    <div className="ai-report">
      <p className="ai-summary"><Rich text={r.summary} /></p>
      {r.unverified ? <p className="ai-note"><Icon name="question" size={16} /> В тексте есть сумма не из расчёта трекера. Сверяйте с цифрами ниже.</p> : null}
      {r.sections.map((s) => (
        <section key={s.heading} className="ai-block">
          <h3>{s.heading}</h3>
          <ul className="ai-points">{s.points.map((p, i) => <li key={i}><Rich text={p} /></li>)}</ul>
        </section>
      ))}
      {r.actions.length ? (
        <section className="ai-block">
          <h3>Что сделать</h3>
          <ul className="ai-actions">{r.actions.map((a, i) => <li key={i}><Icon name="check" size={16} /><span><Rich text={a} /></span></li>)}</ul>
        </section>
      ) : null}
      {facts ? <Facts facts={facts} /> : null}
      {r.caveats.length ? <ul className="ai-caveats">{r.caveats.map((c, i) => <li key={i}><Rich text={c} /></li>)}</ul> : null}
    </div>
  );
}

/** what Codex prepared, at any stage: waiting, the answer, a question back, or the failure */
export function JobView({ job, error, onRetry, onAskAgain, onBrokerSaved }: {
  job: AiJob | null; error: Error | null; onRetry?: () => void; onAskAgain?: (text: string) => void; onBrokerSaved?: () => void;
}) {
  if (!job) return error ? <ErrorBox error={error} /> : <div className="ai-sk" aria-busy="true"><span className="skeleton sk-line" /><span className="skeleton sk-line" /></div>;
  if (job.status === 'queued' || job.status === 'running') return <Steps job={job} />;
  if (job.status === 'failed') return <ErrorBox error={new Error(job.error ?? 'Не получилось подготовить ответ')} onRetry={onRetry} />;
  const r = job.result;
  if (r?.type === 'report') return <div className="ai-fade"><Report r={r} facts={job.facts} /></div>;
  if (r?.type === 'broker') return <div className="ai-fade"><BrokerReview r={r} onSaved={onBrokerSaved} /></div>;
  if (r?.type === 'clarify') {
    return (
      <div className="ai-fade ai-clarify">
        <p><Rich text={r.message} /></p>
        {onAskAgain && job.params.question ? <Button size="sm" onClick={() => onAskAgain(job.params.question!)}>Уточнить вопрос</Button> : null}
      </div>
    );
  }
  return null;
}

// ---------- broker screenshots ----------

/** up to three screenshots: dropped, pasted or chosen; shown as thumbnails before they are sent */
export function ShotPicker({ shots, onChange, busy }: { shots: string[]; onChange: (s: string[]) => void; busy: boolean }) {
  // on a phone the zone is a picker: nothing to drag there
  const mobile = useIsMobile();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState<'ok' | 'bad' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const take = async (files: File[]) => {
    setOver(null);
    const images = files.filter((f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (!images.length) { setError('Нужны картинки: PNG, JPEG или HEIC'); return; }
    const room = 3 - shots.length;
    if (room <= 0) { setError('Не больше трёх скриншотов'); return; }
    setReading(true); setError(null);
    try {
      const added: string[] = [];
      for (const f of images.slice(0, room)) {
        if (f.size > 25 * 1024 * 1024) throw new Error('Файл больше 25 МБ');
        added.push(await screenshotToJpeg(f));
      }
      onChange([...shots, ...added]);
      if (images.length > room) setError('Взяты первые три скриншота');
    } catch (e) { setError((e as Error).message); } finally { setReading(false); }
  };
  const disabled = busy || reading;
  return (
    <div className="ai-shots-picker">
      <div className={cx('icon-drop', 'ai-drop', over === 'ok' && 'over', over === 'bad' && 'reject')} aria-busy={reading}
        onDragOver={(e) => { e.preventDefault(); setOver([...e.dataTransfer.items].some((i) => i.type.startsWith('image/')) ? 'ok' : 'bad'); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null); }}
        onDrop={(e) => { e.preventDefault(); if (!disabled) void take([...e.dataTransfer.files]); }}
        onPaste={(e) => { const files = [...e.clipboardData.files]; if (files.length && !disabled) { e.preventDefault(); void take(files); } }}>
        <span className="tile tile-lg ai-drop-icon" aria-hidden><Icon name="folder" size={22} /></span>
        <div className="icon-drop-text">
          <strong>{over === 'ok' ? 'Отпустите, чтобы добавить' : over === 'bad' ? 'Сюда можно только картинки' : 'Скриншоты портфеля'}</strong>
          <span className="muted small" role="status">{reading ? 'Готовлю скриншоты…' : `Главный экран и экраны счетов, до трёх.${shots.length ? ` Добавлено: ${shots.length}` : mobile ? '' : ' Перетащите сюда или выберите'}`}</span>
        </div>
        <div className="icon-drop-actions">
          <Button size="sm" onClick={() => input.current?.click()} disabled={disabled || shots.length >= 3} busy={reading}>
            {shots.length ? 'Добавить ещё' : 'Выбрать скриншоты'}
          </Button>
        </div>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" multiple hidden
          onChange={(e) => { void take([...(e.target.files ?? [])]); e.target.value = ''; }} />
      </div>
      {error ? <p className="form-error small" role="alert">{error}</p> : null}
      {shots.length ? (
        <ul className="ai-shots">
          {shots.map((s, i) => (
            <li key={i} className="ai-shot">
              <img src={s} alt={`Скриншот ${i + 1}`} />
              <button type="button" className="icon-btn ai-shot-remove" aria-label={`Убрать скриншот ${i + 1}`} disabled={disabled}
                onClick={() => onChange(shots.filter((_, j) => j !== i))}><Icon name="close" size={16} /></button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** the figures Codex read, checked by the owner before they go into the plan year */
function BrokerReview({ r, onSaved }: { r: AiBrokerResult; onSaved?: () => void }) {
  const [valueNow, setValueNow] = useState<number | null>(r.marketValue);
  const [result, setResult] = useState<number | null>(r.marketResult);
  const [date, setDate] = useState(r.asOf);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const today = todayIso();
  const year = Number(date.slice(0, 4));
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today && (valueNow !== null || result !== null);
  const save = async () => {
    setBusy(true);
    try {
      await put<Finplan>(`/finplan/years/${year}/market`, { marketValue: valueNow, marketResult: result, marketDate: date });
      invalidate('/finplan');
      setSaved(true);
      toast(`Финплан ${year} года обновлён`);
      onSaved?.();
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="ai-broker">
      <ul aria-label="Счета на скриншотах">
        {r.accounts.map((a, i) => (
          <li key={i} className="ledger-row">
            <span className="ai-row-text">{a.name}{a.profitAllTime !== null ? (
              <span className={cx('small', a.profitAllTime < 0 ? 'neg' : 'pos')}><span className="money">{rub(a.profitAllTime, { sign: true })}</span> за всё время</span>
            ) : null}</span>
            <span className="ledger-amount">{a.value === null ? '—' : rub(a.value)}</span>
          </li>
        ))}
      </ul>
      <div className="form">
        <Field label="Стоимость портфеля"><NumberInput value={valueNow} onChange={setValueNow} className="num" inputMode="decimal" /></Field>
        <Field label={`Доход за ${year} год`}
          hint={r.marketResult === null ? 'Брокер показал только результат за всё время. Без этой суммы финплан посчитает доход года из стоимости портфеля.' : undefined}>
          <NumberInput value={result} onChange={setResult} className="num" inputMode="decimal" />
        </Field>
        <Field label="На дату" hint={r.asOfShown ? undefined : 'На скриншоте нет даты, стоит день загрузки.'}>
          <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      {r.notes.length ? <ul className="ai-caveats">{r.notes.map((n) => <li key={n}>{n}</li>)}</ul> : null}
      <Button variant="primary" busy={busy} disabled={!valid || saved} onClick={() => void save()}>
        {saved ? <><Icon name="check" size={18} /> Сохранено в финплан</> : 'Сохранить в финплан'}
      </Button>
    </div>
  );
}
